import { createFileRoute, Link } from "@tanstack/react-router";
import { VerifyEmailNotice } from "@/components/auth/VerifyEmailNotice";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { signUp } from "@/lib/auth";
import { captureMarketingOptInFn } from "@/lib/newsletter/newsletter.functions";
import {
  AuthShell,
  Field,
  GoldButton,
  PasswordInput,
  SocialAuthButtons,
  authInputClass,
  errorText,
} from "@/components/auth/AuthShell";

const schema = z.object({
  email: z.string().trim().email("Enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Create Account — CB16" },
      { name: "description", content: "Create your CB16 luxury perfume account." },
      { property: "og:title", content: "Create Account — CB16" },
      { property: "og:description", content: "Create your CB16 luxury perfume account." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SignupPage,
});

function SignupPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>({});
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [optIn, setOptIn] = useState(true);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      const fe = parsed.error.flatten().fieldErrors;
      setErrors({ email: fe.email?.[0], password: fe.password?.[0] });
      return;
    }
    setErrors({});
    setLoading(true);
    const { error } = await signUp(parsed.data.email, parsed.data.password);
    setLoading(false);
    if (error) {
      setErrors({ form: error });
      return;
    }
    setSentTo(parsed.data.email);
    if (optIn) {
      void captureMarketingOptInFn({
        data: { email: parsed.data.email, source: "customer_registration" },
      }).catch(() => undefined);
    }
  }

  if (sentTo) {
    return (
      <AuthShell title="Check your inbox" subtitle="One last step to activate your account">
        <VerifyEmailNotice email={sentTo} accepted />
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create account" subtitle="Join the CB16 maison">
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <Field label="Email" htmlFor="email" error={errors.email}>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={authInputClass}
          />
        </Field>
        <Field label="Password" htmlFor="password" error={errors.password}>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={authInputClass}
          />
        </Field>
        {errors.form && <p className={errorText}>{errors.form}</p>}
        <label className="flex items-start gap-2 text-xs tracking-wide text-black/60">
          <input
            type="checkbox"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            className="mt-[3px] accent-[#C6A15B]"
          />
          Send me CB16 editions, invitations and offers by email.
        </label>
        <GoldButton type="submit" disabled={loading}>
          {loading ? "Creating account…" : "Create Account"}
        </GoldButton>
        <div className="flex items-center gap-3 py-1">
          <span className="h-px flex-1 bg-black/10" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-black/40">or</span>
          <span className="h-px flex-1 bg-black/10" />
        </div>
        <SocialAuthButtons />
        <p className="text-center text-xs tracking-wide text-black/70">
          Already have an account?{" "}
          <Link to="/login" className="text-[#C6A15B] hover:underline">
            Sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
